import { alpha } from '@mui/material';

import { useAppSelector } from 'features/app/hooks';
import {
  DialPadButtonList,
  DialPadCloseButton,
  DialPadCollapseButton,
  DialPadInput,
  DialPadParticipantSelect,
  OpenDialpadButton,
} from 'features/dial-pad/components';
import {
  useAutoLaunchDialPad,
  useDialPadKeyboard,
  useDialPadOperations,
  useDialPadParticipant,
  useDtmfHandler,
  useDtmfSound,
  useDtmfSubscription,
} from 'features/dial-pad/hooks';
import {
  isDialPadOpenedSelector,
  selectIsDialPadCollapsed,
} from 'features/dial-pad/store';
import styled from 'features/theme/styled';
import theme from 'features/theme/theme';

export const DialPad = () => {
  // Redux selectors
  const isDialPadOpened = useAppSelector(isDialPadOpenedSelector);
  const isCollapsed = useAppSelector(selectIsDialPadCollapsed);
  useAutoLaunchDialPad();

  // Custom hooks
  const { participantId, setParticipantId } = useDialPadParticipant();
  const { addSoundToQueue } = useDtmfSound();
  const { dialedNumber, clearDialedNumbers, clearAllAndCloseDialPad } =
    useDialPadOperations();

  // DTMF handling
  const { sendDtmfToHearing, handleIncomingDtmf } = useDtmfHandler({
    participantId,
    onSoundAdd: addSoundToQueue,
  });

  // Keyboard handling
  useDialPadKeyboard({
    onKeyValue: sendDtmfToHearing,
  });

  // DTMF subscription management
  useDtmfSubscription({
    participantId,
    onIncomingDtmf: handleIncomingDtmf,
  });

  // Early return if dial pad is closed
  if (!isDialPadOpened) {
    return (
      <DialPadContainer $noBorder>
        <OpenDialpadButton />
      </DialPadContainer>
    );
  }

  return (
    <>
      <DialPadParticipantSelect
        value={participantId}
        onParticipantSelected={setParticipantId}
      />
      <DialPadContainer id='dialpad-wrapper' data-testid='dialpad-wrapper'>
        <DialPadInput
          dialedNumber={dialedNumber}
          onClear={clearDialedNumbers}
        />
        <DialPadButtonGrid $isCollapsed={isCollapsed}>
          <DialPadButtonList onMouseDown={sendDtmfToHearing} />
        </DialPadButtonGrid>
        <DialPadFooter>
          <DialPadCollapseButton />
          <DialPadCloseButton
            dialedNumber={dialedNumber}
            clearAllAndClose={clearAllAndCloseDialPad}
          />
        </DialPadFooter>
      </DialPadContainer>
    </>
  );
};

// Add displayName for component identification
DialPad.displayName = 'DialPad';

// Styled Components
const DialPadContainer = styled('div', {
  shouldForwardProp: (prop) => prop !== '$noBorder',
})<{ $noBorder?: boolean }>`
  display: flex;
  flex-direction: column;
  user-select: none;
  border: ${({ $noBorder }) =>
    $noBorder
      ? 'none'
      : `solid 0.5px ${alpha(theme.palette.common.white, 0.1)}`};
  background-color: #2f3438;
  width: 100%;
`;

const DialPadButtonGrid = styled('div', {
  shouldForwardProp: (prop) => prop !== '$isCollapsed',
})<{ $isCollapsed?: boolean }>`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  grid-template-rows: repeat(4, 63px);
  gap: 0;
  max-height: ${({ $isCollapsed }) => ($isCollapsed ? '0px' : '252px')};
  overflow: hidden;
`;

const DialPadFooter = styled('div')`
  display: flex;
  border-top: solid 0.5px ${alpha(theme.palette.common.white, 0.1)};
  gap: ${theme.spacing(3)};
  padding: ${theme.spacing(4)};
`;
